import { HelpCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TaskConfig, CapabilityMetadata, ParameterValue } from '../types';
import { RUNTIME_VARIABLES } from '../types';

interface InputTabProps {
    config: TaskConfig;
    capability: CapabilityMetadata | null;
    onInputPathChange: (path: string) => void;
    onParameterChange: (field: string, value: ParameterValue) => void;
    validationErrors: Record<string, string>;
    readOnly: boolean;
}

export function InputTab({
    config,
    capability,
    onInputPathChange,
    onParameterChange,
    validationErrors,
    readOnly,
}: InputTabProps) {
    const inputSchema = capability?.inputSchema;
    const requiredFields = inputSchema?.required || [];
    const properties = inputSchema?.properties || {};

    return (
        <div className="p-6 space-y-8">
            {/* Input Selector */}
            <section className="space-y-3">
                <div className="flex items-center gap-2">
                    <Label className="text-base font-semibold">Input Selector</Label>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger>
                                <HelpCircle size={14} className="text-slate-400" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                <p>
                                    JSONPath expression to select which part of the workflow state
                                    is passed to this task. Default <code>$</code> passes everything.
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <Input
                    type="text"
                    value={config.inputPath}
                    onChange={(e) => onInputPathChange(e.target.value)}
                    placeholder="$ (entire state)"
                    className="font-mono"
                    disabled={readOnly}
                />
                <p className="text-xs text-slate-500">
                    Examples: <code className="bg-slate-100 px-1 rounded">$</code> (all),{' '}
                    <code className="bg-slate-100 px-1 rounded">$.documents</code>,{' '}
                    <code className="bg-slate-100 px-1 rounded">$.input.caseId</code>
                </p>
            </section>

            {/* Field Mappings */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Field Mapping</Label>
                    {!capability && (
                        <span className="text-sm text-slate-400">
                            Select a capability to see input fields
                        </span>
                    )}
                </div>

                {Object.entries(properties).length > 0 ? (
                    <div className="space-y-4">
                        {Object.entries(properties).map(([fieldName, fieldSchema]) => {
                            const isRequired = requiredFields.includes(fieldName);
                            const paramValue = config.parameters[fieldName] || {
                                source: 'constant' as const,
                                value: '',
                            };
                            const hasError = validationErrors[`param.${fieldName}`];

                            return (
                                <div
                                    key={fieldName}
                                    className={cn(
                                        "rounded-lg border p-4",
                                        hasError ? "border-red-300 bg-red-50/50" : "border-slate-200"
                                    )}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-mono text-sm font-medium">
                                                    {fieldName}
                                                </span>
                                                <Badge
                                                    variant="secondary"
                                                    className="text-[10px] font-mono"
                                                >
                                                    {fieldSchema.type}
                                                </Badge>
                                                {isRequired && (
                                                    <Badge className="text-[10px] bg-red-100 text-red-700 hover:bg-red-100">
                                                        required
                                                    </Badge>
                                                )}
                                            </div>
                                            {fieldSchema.description && (
                                                <p className="text-xs text-slate-500 mt-0.5">
                                                    {fieldSchema.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        {/* Source selector */}
                                        <Select
                                            value={paramValue.source}
                                            onValueChange={(source: ParameterValue['source']) =>
                                                onParameterChange(fieldName, {
                                                    ...paramValue,
                                                    source,
                                                    value: source === 'runtime' ? 'executionId' : '',
                                                })
                                            }
                                            disabled={readOnly}
                                        >
                                            <SelectTrigger className="w-[140px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="state">From State</SelectItem>
                                                <SelectItem value="runtime">Runtime</SelectItem>
                                                <SelectItem value="constant">Constant</SelectItem>
                                            </SelectContent>
                                        </Select>

                                        {/* Value input based on source */}
                                        {paramValue.source === 'state' && (
                                            <Input
                                                type="text"
                                                value={paramValue.value}
                                                onChange={(e) =>
                                                    onParameterChange(fieldName, {
                                                        ...paramValue,
                                                        value: e.target.value,
                                                    })
                                                }
                                                placeholder="$.path.to.value"
                                                className="flex-1 font-mono text-sm"
                                                disabled={readOnly}
                                            />
                                        )}

                                        {paramValue.source === 'runtime' && (
                                            <Select
                                                value={paramValue.value}
                                                onValueChange={(value) =>
                                                    onParameterChange(fieldName, {
                                                        ...paramValue,
                                                        value,
                                                    })
                                                }
                                                disabled={readOnly}
                                            >
                                                <SelectTrigger className="flex-1">
                                                    <SelectValue placeholder="Select variable" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {RUNTIME_VARIABLES.map((v) => (
                                                        <SelectItem key={v.key} value={v.key}>
                                                            <div className="flex flex-col items-start">
                                                                <span>{v.label}</span>
                                                                <span className="text-xs text-slate-400">
                                                                    {v.description}
                                                                </span>
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        )}

                                        {paramValue.source === 'constant' && (
                                            <Input
                                                type="text"
                                                value={paramValue.value}
                                                onChange={(e) =>
                                                    onParameterChange(fieldName, {
                                                        ...paramValue,
                                                        value: e.target.value,
                                                    })
                                                }
                                                placeholder={
                                                    fieldSchema.default !== undefined
                                                        ? `Default: ${fieldSchema.default}`
                                                        : 'Enter value'
                                                }
                                                className="flex-1"
                                                disabled={readOnly}
                                            />
                                        )}
                                    </div>

                                    {hasError && (
                                        <p className="text-xs text-red-600 mt-1">{hasError}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : capability ? (
                    <div className="text-center py-8 text-slate-500 border border-dashed border-slate-200 rounded-lg">
                        This capability has no input parameters defined.
                    </div>
                ) : (
                    <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                        Select a capability in the Capability tab to configure inputs.
                    </div>
                )}
            </section>
        </div>
    );
}

